/*
 * Shilajit Global — hero video. Scroll-scrubbed on desktop, plain autoplaying
 * background video on phones, with a shared intro playback mode.
 *
 * WHY THE TWO PATHS DIVERGE
 * Scrubbing works by writing currentTime, and every write makes the decoder
 * walk from the previous keyframe. That cost is paid per seek and it does not
 * go away on fast hardware: it was still visibly laggy on a Snapdragon 8 Elite
 * flagship. It is a property of seeking compressed video in a mobile browser,
 * not of any particular handset. So phones no longer scrub at all — they get
 * the portrait cut playing itself, which is what video decoders are actually
 * built to do. Desktop keeps the scrub, where it demonstrably works.
 *
 * MOBILE BEHAVIOUR — the two switches worth knowing about
 *   MOBILE_MODE  'play'  plain autoplaying background video (default)
 *                'scrub' put phones back on the scroll-scrub path
 *   MOBILE_LOOP  false   play once and hold on the final frame (default)
 *                true    loop forever
 * Both are single constants at the top of the setup block. Flipping either is
 * a one-word edit; neither touches the section or its schema.
 *
 * SOURCE SELECTION
 * The markup ships with NO src and preload="none", carrying both candidate
 * URLs as data attributes. This script picks one and assigns it. Doing it in
 * JS rather than markup matters: <source media="..."> inside <video> is no
 * longer honoured by Chrome or WebKit, and two <video> elements toggled with
 * CSS would download both files. This way a phone never fetches the landscape
 * cut, which on a hero-sized video is most of the page weight.
 *
 * The choice is made once, on load, at <=749px. That single decision also
 * selects the behaviour path, so there is exactly one definition of "mobile"
 * in this file. Rotating the device does not re-pick, because swapping src
 * mid-session throws away the buffer and restarts the download for a change
 * of framing nobody asked for.
 *
 * THREE MODES
 *
 * 1. Intro (first arrival, or once per N hours — see sections/sg-intro-boot).
 *    Plays from the start as a fixed full-screen overlay with the header
 *    hidden. Runs on both paths. Any interaction, the video ending, or a
 *    refused autoplay ends it.
 *
 * 2. Mobile playback. The runway is never created, so the hero is an ordinary
 *    100vh section from first paint — there is no tall container to collapse
 *    later and therefore no scroll gap and no jump. The poster holds until the
 *    decoder has a frame, which is the element's own behaviour, then the video
 *    plays. No scroll listener, no rAF loop, no seeks.
 *
 * 3. Scrub (desktop). Scroll position maps to currentTime. The runway is a
 *    tall section; the video is sticky inside it. At the end of the runway the
 *    pin releases and the page continues — native scroll is never blocked.
 *    Runway length is derived from the actual duration, plus an optional hold
 *    zone described below.
 *
 * MOTION MODEL (scrub mode)
 *     alpha = 1 - exp(-dt / TAU)
 *
 * Exponential decay gives "keeps moving, sheds speed, glides to a stop": the
 * remaining distance shrinks by a constant proportion per unit time. A settle
 * is ~99% done after 5 x TAU, so TAU = settle/5. dt-based rather than a fixed
 * per-frame factor, because a fixed factor runs at different speeds on 60Hz
 * and 120Hz displays and stalls exactly when the frame rate is already poor.
 *
 * dt IS REPORTED HONESTLY, WITH A CEILING
 * A long frame must be told to the easing as a long frame. Substituting a
 * nominal 16ms for any frame over 100ms — which is what this used to do —
 * makes the video advance roughly six times less than the elapsed time
 * warrants, so the image falls progressively further behind the scroll and
 * the glide degrades into a crawl. Real elapsed time is used instead, capped
 * at DT_MAX so that a tab switch, a GC pause or a backgrounded rAF cannot
 * teleport the playhead on the first frame back. The exponential form keeps
 * this frame-rate independent: two 8ms frames and one 16ms frame still land
 * in the same place.
 *
 * READINESS GATE (scrub path only)
 * Metadata arriving says only that the duration is known. It says nothing
 * about whether any media data exists to seek into. Confirmed on a real
 * device on cellular: without this the first seeks fire at an empty buffer.
 * So: after metadata, issue exactly one priming seek to the entry point (a
 * decoder will not buffer a region nobody has asked for), then wait for a
 * contiguous buffered range covering that point before the scrub loop starts.
 * Until then the poster holds. If readiness never arrives, collapse to the
 * static poster hero — but only while the viewer is still at the top of the
 * runway, because removing several thousand pixels of runway underneath
 * someone who has already scrolled into it would yank the page.
 *
 * WHY SCRUBBING USED TO FEEL CHOPPY
 * Seeking compressed video is expensive; the decoder walks from the previous
 * keyframe. Issuing a seek while one is in flight queues work it cannot
 * service. So: never seek while seeking, and skip seeks under one frame.
 *
 * NO-OP SEEKS MUST NOT LATCH THE SERIALISER
 * The in-flight flag is cleared by the 'seeked' event, and assigning
 * currentTime a value it already holds fires no such event. The old pre-seek
 * test compared the unclamped target against currentTime, so at either end of
 * the runway — where clampTime pins the value — it could ask for a seek that
 * the browser correctly treated as nothing to do, leaving the flag set for
 * good. Both the test and requestSeek now work in clamped time, and
 * requestSeek refuses to arm the flag for a seek that would not move the
 * playhead. 'stalled', 'abort' and 'error' also release it.
 *
 * INTRO -> SCRUB TAKEOVER, AND THE WATCHED GATE (5 Sep 2026)
 * Two behaviour changes, both desktop-path only.
 *
 * Takeover: ending the intro used to hand the scrub a scroll position of 0,
 * so the video snapped back to its first frame the moment the visitor
 * scrolled. Now endIntro reads the playhead and jumps window scroll to the
 * runway position that maps to it (sectionTop + t * pxPerSecond) before the
 * scrub starts. The stage is sticky and identical at every runway position,
 * so the jump itself is invisible; the visitor's scroll simply continues the
 * film from where playback had reached. An intro that plays to 'ended' maps
 * to the start of the hold zone, which locks the hero and lands the visitor
 * directly in the shoppable state.
 *
 * Watched gate: when the video completes — intro 'ended', or the scrub
 * reaching lock — a timestamp is stored (localStorage, same mechanism and
 * lifetime as the intro's own key; per BROWSER, not per IP, because a theme
 * has no server to see an address with). While it is fresher than the
 * intro's "Show again after (hours)" editor setting (sgIntro.interval,
 * falling back to one hour), every load skips both the intro and the scrub
 * runway entirely: the section is 100vh plus the hold zone, the playhead is
 * pinned to the final frame, and the hero opens already locked with the
 * hotspot layer live. The stamp is written only on completion, so a visitor
 * who interrupted the film mid-way still gets the scrub next time.
 *
 * THE LOCK IS ONE-WAY. The moment the scrub reaches lock, the hero converts
 * to resume-lock IN PLACE: the runway collapses to the hold zone and the
 * scroll position is adjusted by exactly the removed height in the same
 * synchronous step, so nothing moves on screen. From then on scrolling back
 * up shows the locked interactive frame, not a rewinding film — the film
 * plays again only when the watched window has expired on a later load.
 * Collapsing at lock rather than on the next visit is what makes the very
 * first pass consistent: finish the film, and it is finished.
 */
(function () {
  'use strict';

  var section = document.querySelector('[data-sg-scroll-hero]');
  if (!section) return;

  var video = section.querySelector('video');
  var sticky = section.querySelector('.sg-scrollhero__sticky');
  if (!video || !sticky) return;

  var introCfg = window.sgIntro || { active: false };
  var introPending = !!introCfg.active;
  var introRunning = false;

  /* The watched gate. See the header comment. One knob governs both the
     intro and this: the sg-intro-boot section's "Show again after (hours)"
     setting, delivered as sgIntro.interval. */
  var WATCH_KEY = 'sg_hero_watched_at';
  var WATCH_TTL = introCfg.interval > 0 ? introCfg.interval : 3600000;

  function watchedRecently() {
    var at = 0;
    try { at = parseInt(localStorage.getItem(WATCH_KEY), 10) || 0; } catch (e) {}
    return (Date.now() - at) < WATCH_TTL;
  }

  function markWatched() {
    try { localStorage.setItem(WATCH_KEY, String(Date.now())); } catch (e) {}
  }

  function clearIntroChrome() {
    document.documentElement.classList.remove('sg-intro');
  }

  // Drop the runway and unpin the stage: an ordinary full-height hero. Used
  // both by collapse() and by the mobile path, which never builds a runway in
  // the first place and so has nothing to give back.
  function makeStatic() {
    section.classList.add('sg-scrollhero--static');
    section.style.height = '';
  }

  function collapse() {
    makeStatic();
    introPending = false;
    clearIntroChrome();
    teardownGate();
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    collapse();
    return;
  }

  /* ------------------------------------------------------- pick the source */

  var srcDesktop = video.getAttribute('data-src') || '';
  var srcMobile = video.getAttribute('data-src-mobile') || '';
  var posterMobile = video.getAttribute('data-poster-mobile') || '';

  var wantMobile = false;
  try {
    wantMobile = window.matchMedia('(max-width: 749px)').matches;
  } catch (e) {}

  // The single definition of "mobile" in this file: one breakpoint test, whose
  // result picks both the file and the behaviour.
  var usingMobileSource = !!(wantMobile && srcMobile);
  var chosen = usingMobileSource ? srcMobile : srcDesktop;

  if (!chosen) { collapse(); return; }   // nothing to play; poster stands in

  if (usingMobileSource) {
    section.setAttribute('data-using', 'mobile');
    if (posterMobile) video.setAttribute('poster', posterMobile);
  } else {
    section.setAttribute('data-using', 'desktop');
  }

  /* ----------------------------------------------------------------- setup */

  // How phones behave. See the note at the top of the file.
  var MOBILE_MODE = 'play';    // 'play' | 'scrub'
  var MOBILE_LOOP = false;     // false = play once, hold the final frame

  var mobilePlay = usingMobileSource && MOBILE_MODE === 'play';

  section.setAttribute('data-mode', mobilePlay ? 'play' : 'scrub');

  /* Resume-lock: the visitor finished the film inside the window, so this
     load goes straight to the shoppable final frame. Decided once, here, and
     the intro chrome is cleared immediately rather than at metadata time so
     the header is not held hidden for the seconds a cold video load takes. */
  var resumeLock = !mobilePlay && watchedRecently();
  if (resumeLock) {
    introPending = false;
    clearIntroChrome();
  }

  var pxPerSecond = parseInt(section.getAttribute('data-scroll-speed'), 10) || 320;
  var settleMs = parseInt(section.getAttribute('data-settle'), 10) || 1200;

  /* HOLD ZONE — runway past the end of the video during which the stage stays
     pinned and the playhead stays on the final frame.

     Without it the locked state has zero scroll distance. The sticky stage
     unpins at exactly the moment progress reaches 1, because that is when the
     section's bottom edge meets the viewport bottom — so there is no interval
     in which the hero sits still on its last frame. The hold zone creates one.

     Defaults to 0, which means a page that has not opted in behaves precisely
     as it did before this existed. That is deliberate: it is what makes the
     change safe to ship ahead of the feature that consumes it, and it is the
     property the regression test checks. */
  var holdPx = parseInt(section.getAttribute('data-hold'), 10) || 0;

  /* Lock state, with hysteresis so a scroll that settles on the boundary does
     not flicker the hotspot layer on and off. Nothing else reads scroll
     position to decide this — there is one source of truth and it emits an
     event. */
  var LOCK_ON = 0.995;
  var LOCK_OFF = 0.97;
  var locked = false;

  // Longest frame the easing will believe. Above this we are no longer
  // animating, we are resuming, and a full catch-up in one step looks like a
  // jump cut. At the 1200ms default settle (TAU = 0.24s) a 250ms frame closes
  // about 65% of the remaining distance — fast, but still a movement.
  var DT_MAX = 0.25;

  // Readiness gate. MIN_BUFFER_AHEAD is deliberately small: this is a check
  // that the decoder has something to seek into around the entry point, not
  // an attempt to preload the whole runway.
  var MIN_BUFFER_AHEAD = 2;        // seconds of contiguous data around entry
  var READY_TIMEOUT_MS = 15000;
  var READY_EVENTS = ['progress', 'canplay', 'canplaythrough', 'loadeddata', 'seeked'];

  var duration = 0;
  var target = 0;
  var current = 0;
  var lastFrameTime = 0;
  var rafId = null;
  var seeking = false;
  var pendingSeek = -1;

  var scrubStarted = false;
  var gateArmed = false;
  var primed = false;
  var readyTimer = null;
  var mobileStarted = false;

  function setRunway() {
    /* Under resume-lock there is nothing to scrub, so the runway is just the
       hold zone: scrollProgress()'s scrubbable term is then <= 0 and it pins
       progress at 1, which is what keeps the lock held. The resize listener
       calls this too, so the distinction must live here, not at the call
       site. */
    var px = resumeLock ? holdPx : Math.round(duration * pxPerSecond + holdPx);
    section.style.height = 'calc(100vh + ' + px + 'px)';
  }

  function scrollProgress() {
    var rect = section.getBoundingClientRect();
    /* The hold zone is runway the scrub does not consume: progress reaches 1
       when the last frame lands, then stays there while the remaining holdPx
       scrolls past and the stage is still pinned. */
    var scrubbable = rect.height - window.innerHeight - holdPx;
    if (scrubbable <= 0) return 1;
    return Math.max(0, Math.min(1, -rect.top / scrubbable));
  }

  /* Convert the live scrub into resume-lock without anything moving on
     screen: shrink the runway to the hold zone and pull the scroll position
     up by exactly the height that vanished, in one synchronous step before
     the next paint. After this, scrollProgress() is pinned at 1, so the
     lock can never release and the scrub never runs again this page view. */
  function enterResumeLock() {
    resumeLock = true;
    var before = section.getBoundingClientRect().height;
    setRunway();
    var removed = before - section.getBoundingClientRect().height;
    if (removed > 0) {
      try { window.scrollBy({ top: -removed, left: 0, behavior: 'instant' }); }
      catch (e) { window.scrollBy(0, -removed); }
    }
    teardownGate();
  }

  function setLocked(next) {
    if (next === locked) return;
    locked = next;
    section.classList.toggle('sg-scrollhero--locked', locked);

    // Reaching the lock by scrubbing counts as having watched the film, and
    // finishes it for good: stamp the watched window and collapse to
    // resume-lock in place (see the header comment — the lock is one-way).
    // Not under resume-lock, though: re-stamping there would slide the
    // window forward on every visit and the video would never play again.
    if (locked && !resumeLock) {
      markWatched();
      enterResumeLock();
    }

    try {
      section.dispatchEvent(new CustomEvent('sg:hero-lock', {
        detail: { locked: locked },
        bubbles: true
      }));
    } catch (e) {}
  }

  function clampTime(t) {
    return Math.max(0.02, Math.min(duration - 0.06, t));
  }

  function requestSeek(t) {
    var want = clampTime(t);

    // A seek to where we already are fires no 'seeked', so arming the flag for
    // one would strand the serialiser permanently.
    if (Math.abs(want - video.currentTime) < 0.001) {
      seeking = false;
      pendingSeek = -1;
      return;
    }

    if (seeking) { pendingSeek = want; return; }
    seeking = true;
    pendingSeek = -1;
    try { video.currentTime = want; }
    catch (e) { seeking = false; }
  }

  video.addEventListener('seeked', function () {
    seeking = false;
    var next = pendingSeek;
    pendingSeek = -1;
    if (next >= 0 && Math.abs(next - video.currentTime) > FRAME) requestSeek(next);
  });

  // The flag is normally cleared by 'seeked'. These are the cases where that
  // event will never arrive: the network gave up, the load was replaced, or
  // the element errored.
  function unlatch() {
    seeking = false;
  }
  ['stalled', 'abort', 'error'].forEach(function (ev) {
    video.addEventListener(ev, unlatch);
  });

  var FPS = 25;
  var FRAME = 1 / FPS;

  function frame(now) {
    rafId = null;
    if (introRunning) return;

    var dt = (now - lastFrameTime) / 1000;
    lastFrameTime = now;
    if (!(dt > 0)) dt = 0.016;
    else if (dt > DT_MAX) dt = DT_MAX;

    var progress = scrollProgress();
    target = progress * duration;

    if (locked) {
      if (progress < LOCK_OFF) setLocked(false);
    } else if (progress >= LOCK_ON) {
      setLocked(true);
    }

    var alpha = 1 - Math.exp(-dt / TAU);
    current += (target - current) * alpha;

    var want = clampTime(current);
    if (Math.abs(want - video.currentTime) > FRAME * 0.5) requestSeek(want);
    if (Math.abs(target - current) > FRAME * 0.5 || seeking) schedule();
  }

  var TAU = (settleMs / 1000) / 5;

  function schedule() {
    if (rafId === null) {
      lastFrameTime = performance.now();
      rafId = window.requestAnimationFrame(frame);
    }
  }

  function kick() {
    if (introRunning) return;
    if (rafId === null) lastFrameTime = performance.now();
    schedule();
  }

  function startScrub() {
    clearIntroChrome();
    try { video.pause(); } catch (e) {}
    current = scrollProgress() * duration;
    requestSeek(current);
    kick();
  }

  /* ------------------------------------------------------- mobile playback */

  function retryPlay() {
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function startMobilePlay() {
    if (mobileStarted) return;
    mobileStarted = true;

    clearIntroChrome();
    makeStatic();

    video.loop = MOBILE_LOOP;

    // If the intro ran to completion the video is already sitting on its last
    // frame, which is exactly where hold-on-last-frame wants it. Calling play()
    // here would rewind to zero and run it a second time.
    if (!MOBILE_LOOP && video.ended) return;

    var p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        // Autoplay refused — Data Saver, battery saver, or an OS policy. Leave
        // the poster up rather than showing a dead black box, and take the
        // next gesture as permission.
        window.addEventListener('touchstart', retryPlay, { once: true, passive: true });
        window.addEventListener('click', retryPlay, { once: true });
      });
    }
  }

  /* -------------------------------------------------------- readiness gate */

  function bufferedCovers(t) {
    var ranges;
    try { ranges = video.buffered; } catch (e) { return false; }
    if (!ranges || !ranges.length) return false;

    var need = Math.min(t + MIN_BUFFER_AHEAD, duration);
    for (var i = 0; i < ranges.length; i++) {
      if (ranges.start(i) <= t + 0.05 && ranges.end(i) >= need - 0.05) return true;
    }
    return false;
  }

  function scrubReady() {
    if (video.readyState < 2) return false;   // < HAVE_CURRENT_DATA
    return bufferedCovers(clampTime(scrollProgress() * duration));
  }

  function teardownGate() {
    if (readyTimer) { clearTimeout(readyTimer); readyTimer = null; }
    if (!gateArmed) return;
    gateArmed = false;
    for (var i = 0; i < READY_EVENTS.length; i++) {
      video.removeEventListener(READY_EVENTS[i], gateCheck);
    }
  }

  function beginScrub() {
    if (scrubStarted) return;
    scrubStarted = true;
    teardownGate();
    startScrub();
  }

  function gateCheck() {
    if (scrubStarted) return;
    if (scrubReady()) { beginScrub(); return; }

    // One priming seek, so the decoder knows which region to fetch. Without it
    // the entry point can stay unbuffered forever and the gate never opens.
    if (!primed) {
      primed = true;
      requestSeek(scrollProgress() * duration);
    }
  }

  function armGate() {
    if (scrubStarted || gateArmed) return;
    gateArmed = true;

    for (var i = 0; i < READY_EVENTS.length; i++) {
      video.addEventListener(READY_EVENTS[i], gateCheck);
    }

    readyTimer = setTimeout(function () {
      readyTimer = null;
      if (scrubStarted) return;
      // Still nothing to scrub. Drop to the static poster hero if the viewer
      // has not moved into the runway; otherwise start anyway, because
      // removing the runway under them is worse than a slow scrub.
      if (scrollProgress() < 0.02) collapse();
      else beginScrub();
    }, READY_TIMEOUT_MS);

    gateCheck();
  }

  /* ---------------------------------------------------------------- intro */

  var INTERACTIONS = ['wheel', 'touchstart', 'keydown', 'click'];
  var introTimer = null;

  function afterIntro() {
    if (mobilePlay) startMobilePlay();
    else armGate();
  }

  /* THE TAKEOVER JUMP. Move window scroll to the runway position that maps
     to the playhead, so the scrub continues the film from where playback
     reached instead of snapping back to frame one. Instant, and invisible:
     the sticky stage renders identically at every position inside the
     runway, so the only thing that changes is the scrollbar. Must run AFTER
     clearIntroChrome — the intro locks body overflow, and a window scroll
     while it is locked goes nowhere. behavior:'instant' is spelled out
     because a theme-level scroll-behavior:smooth would otherwise animate
     this, which would visibly scrub the video. Never jumps backwards. */
  function syncScrollToPlayhead(t) {
    if (mobilePlay || !duration || !(t > 0)) return;
    var rect = section.getBoundingClientRect();
    var sectionTop = (window.pageYOffset || 0) + rect.top;
    var want = sectionTop + Math.min(t, duration) * pxPerSecond;
    if ((window.pageYOffset || 0) >= want) return;
    try { window.scrollTo({ top: want, left: 0, behavior: 'instant' }); }
    catch (e) { window.scrollTo(0, want); }
  }

  function endIntro() {
    if (!introRunning) return;
    introRunning = false;
    introPending = false;

    // Read these before anything below can move the playhead. A completed
    // intro maps to the full duration, which lands in the hold zone: the
    // hero locks and the visitor arrives directly in the shoppable state.
    var playhead = video.currentTime || 0;
    var finished = !!video.ended;
    if (finished) { playhead = duration; markWatched(); }

    if (introTimer) { clearTimeout(introTimer); introTimer = null; }
    INTERACTIONS.forEach(function (ev) {
      window.removeEventListener(ev, endIntro, true);
    });

    try { localStorage.setItem(introCfg.key, String(Date.now())); } catch (e) {}

    // Reveal the header now rather than when playback or scrubbing eventually
    // starts — the gate below may hold for several seconds on a slow link.
    clearIntroChrome();
    syncScrollToPlayhead(playhead);
    afterIntro();
  }

  function startIntro() {
    introRunning = true;

    INTERACTIONS.forEach(function (ev) {
      window.addEventListener(ev, endIntro, { once: true, capture: true, passive: true });
    });

    video.addEventListener('ended', endIntro, { once: true });
    introTimer = setTimeout(endIntro, (duration + 4) * 1000);

    try { video.currentTime = 0; } catch (e) {}

    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(endIntro);
  }

  /* --------------------------------------------------------------- start */

  /* Resume-lock start. No scrub loop and no readiness gate: progress is
     pinned at 1 by the hold-only runway, the lock is set directly (which
     also arms the hotspot layer via its event), and one seek parks the
     playhead on the final frame. On desktop-gate viewports the Phase 2
     still covers the stage the moment the lock class lands, so nothing
     waits on the video fetching its last keyframe. */
  function startLockedResume() {
    clearIntroChrome();
    current = duration;
    target = duration;
    setLocked(true);
    requestSeek(duration);
  }

  video.addEventListener('loadedmetadata', function () {
    duration = video.duration || 0;
    if (!duration || !isFinite(duration)) { collapse(); return; }

    // No runway on the mobile path — the section stays its natural 100vh, so
    // there is never a tall container to take away later.
    if (!mobilePlay) setRunway();

    if (resumeLock) startLockedResume();
    else if (introPending) startIntro();
    else afterIntro();
  });

  video.addEventListener('error', collapse);

  // Scroll and resize only matter to the scrub path. On phones nothing reads
  // scroll position, so nothing is listening.
  if (!mobilePlay) {
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', function () {
      // Recompute the runway (viewport height changed) but never re-pick the
      // source — see the note at the top.
      if (duration) setRunway();
      kick();
    });
  }

  // Long stop: if metadata never arrives, do not leave the header hidden
  // forever. Generous, because a big hero legitimately takes ~20s.
  setTimeout(function () {
    if (!duration) {
      introPending = false;
      clearIntroChrome();
    }
  }, 40000);

  video.src = chosen;
  video.preload = 'auto';
  try { video.load(); } catch (e) {}
})();
